// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ScoutCouponCode, CreateScoutCouponRequest, ScoutCouponValidationResult } from '@/types/scoutCoupons';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

export const useScoutCoupons = (scoutId?: string) => {
  const [coupons, setCoupons] = useState<ScoutCouponCode[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch scout coupons
  const fetchCoupons = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('scout_coupons')
        .select(`
          *,
          memberships(name, slug, price_cents)
        `);
      
      if (scoutId) {
        query = query.eq('scout_id', scoutId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching scout coupons:', error);
        return;
      }
      
      setCoupons(data || []);
    } catch (err) {
      console.error('Error fetching scout coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [scoutId]);

  // Create new coupon
  const createCoupon = async (couponData: CreateScoutCouponRequest): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('scout_coupons')
        .insert([couponData])
        .select()
        .single();

      if (error) {
        console.error('Error creating scout coupon:', error);
        return false;
      }

      // Refresh the list
      await fetchCoupons();
      return true;
    } catch (err) {
      console.error('Error creating scout coupon:', err);
      return false;
    }
  };

  // Update coupon
  const updateCoupon = async (couponId: string, updates: Partial<ScoutCouponCode>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('scout_coupons')
        .update(updates)
        .eq('id', couponId);

      if (error) {
        console.error('Error updating scout coupon:', error);
        return false;
      }

      // Refresh the list
      await fetchCoupons();
      return true;
    } catch (err) {
      console.error('Error updating scout coupon:', err);
      return false;
    }
  };

  // Delete coupon
  const deleteCoupon = async (couponId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('scout_coupons')
        .delete()
        .eq('id', couponId);

      if (error) {
        console.error('Error deleting scout coupon:', error);
        return false;
      }

      // Refresh the list
      await fetchCoupons();
      return true;
    } catch (err) {
      console.error('Error deleting scout coupon:', err);
      return false;
    }
  };

  // Validate coupon
  const validateCoupon = async (code: string, originalPrice: number): Promise<ScoutCouponValidationResult> => {
    try {
      const { data: { user } } = await getSessionUser();
      
      const { data, error } = await invokeEdgeFunction('validate-scout-coupon-code', {
        body: { 
          code, 
          original_price: originalPrice,
          user_id: user?.id
        }
      });

      if (error) {
        console.error('Error validating scout coupon:', error);
        return { valid: false, error: 'Failed to validate coupon' };
      }

      return data;
    } catch (err) {
      console.error('Error validating scout coupon:', err);
      return { valid: false, error: 'Failed to validate coupon' };
    }
  };

  return {
    coupons,
    loading,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    validateCoupon,
    refreshCoupons: fetchCoupons
  };
};